import { formatBrl } from '@estac1/shared';
import { Badge, Button, Card, CardContent, Skeleton } from '@estac1/ui';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import type { DriftResponse, RecalcResponse } from './types.js';

interface PricingDriftCardProps {
  drift: UseQueryResult<DriftResponse>;
  recalc: UseMutationResult<RecalcResponse, unknown, void, unknown>;
  onRecalc: () => void;
}

export const PricingDriftCard = ({ drift, recalc, onRecalc }: PricingDriftCardProps) => (
  <Card>
    <CardContent className="space-y-3 pt-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 shrink-0 text-warning" />
          <h3 className="text-sm font-semibold">Clientes em preço antigo</h3>
        </div>
        <Button
          size="sm"
          variant="outline"
          className="w-full sm:w-auto"
          onClick={onRecalc}
          disabled={recalc.isPending || (drift.data?.count ?? 0) === 0}
        >
          <RefreshCw className={recalc.isPending ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
          Recalcular todos
        </Button>
      </div>
      {drift.isLoading ? (
        <Skeleton className="h-20 w-full" />
      ) : (drift.data?.count ?? 0) === 0 ? (
        <p className="text-xs text-muted-fg">Todas as assinaturas ativas estão no preço atual.</p>
      ) : (
        <ul className="space-y-2">
          {drift.data?.drift.map((row) => (
            <li
              key={row.lotId}
              className="flex flex-wrap items-baseline justify-between gap-2 rounded border border-border bg-surface px-3 py-2 text-sm"
            >
              <span className="font-medium">{row.lotName}</span>
              <span className="text-xs text-muted-fg">{row.vacancies} vagas</span>
              <span className="font-mono text-xs">
                {row.snapshotTotalCents != null ? formatBrl(row.snapshotTotalCents) : '—'} →{' '}
                <span className="font-semibold">{formatBrl(row.currentTotalCents)}</span>
              </span>
              {row.deltaCents === null ? (
                <Badge variant="default" className="font-mono text-xs">
                  sem preço gravado
                </Badge>
              ) : (
                <Badge
                  variant={row.deltaCents > 0 ? 'warning' : 'default'}
                  className="font-mono text-xs"
                >
                  {row.deltaCents > 0 ? '+' : ''}
                  {formatBrl(row.deltaCents)}
                </Badge>
              )}
            </li>
          ))}
        </ul>
      )}
    </CardContent>
  </Card>
);
